import { useState, useEffect, useRef } from "react";
import { X, Download, Smartphone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { usePWAInstall } from "@/hooks/usePWAInstall";

// Extend window for analytics tracking
declare global {
  interface Window {
    ohTrack?: (eventType: string, eventName: string, metadata?: Record<string, unknown>) => void;
  }
}

const DISMISSED_KEY = "pwa-install-banner-dismissed";
const DISMISS_DURATION = 7 * 24 * 60 * 60 * 1000; // 7 days

const trackEvent = (eventName: string, metadata?: Record<string, unknown>) => {
  if (window.ohTrack) {
    window.ohTrack("pwa_banner", eventName, metadata);
  }
};

export function PWAInstallBanner() {
  const { isInstallable, isInstalled, promptInstall } = usePWAInstall();
  const [isDismissed, setIsDismissed] = useState(true);
  const [isVisible, setIsVisible] = useState(false);
  const hasTrackedShown = useRef(false);

  useEffect(() => {
    // Check if banner was previously dismissed
    const dismissedAt = localStorage.getItem(DISMISSED_KEY);
    if (dismissedAt) {
      const dismissedTime = parseInt(dismissedAt, 10);
      if (Date.now() - dismissedTime < DISMISS_DURATION) {
        setIsDismissed(true);
        return;
      }
    }
    setIsDismissed(false);
  }, []);

  useEffect(() => {
    // Show banner with slight delay for better UX
    if (isInstallable && !isDismissed && !isInstalled) {
      const timer = setTimeout(() => setIsVisible(true), 2000);
      return () => clearTimeout(timer);
    }
    setIsVisible(false);
  }, [isInstallable, isDismissed, isInstalled]);

  // Track when banner becomes visible
  useEffect(() => {
    if (isVisible && !hasTrackedShown.current) {
      trackEvent("banner_shown", { page: window.location.pathname });
      hasTrackedShown.current = true;
    }
  }, [isVisible]);

  const handleDismiss = () => {
    trackEvent("banner_dismissed", { page: window.location.pathname });
    setIsVisible(false);
    localStorage.setItem(DISMISSED_KEY, Date.now().toString());
    setTimeout(() => setIsDismissed(true), 300);
  };

  const handleInstall = async () => {
    trackEvent("install_clicked", { page: window.location.pathname });
    const installed = await promptInstall();
    if (installed) {
      trackEvent("install_completed", { page: window.location.pathname });
      setIsVisible(false);
      setIsDismissed(true);
    } else {
      trackEvent("install_cancelled", { page: window.location.pathname });
    }
  };

  if (isDismissed || isInstalled || !isInstallable) {
    return null;
  }

  return (
    <div
      className={`fixed bottom-0 left-0 right-0 z-50 transition-transform duration-300 ease-out ${
        isVisible ? "translate-y-0" : "translate-y-full"
      }`}
    >
      <div className="bg-gradient-to-r from-primary to-accent p-4 shadow-lg animate-fade-in">
        <div className="max-w-4xl mx-auto flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <div className="w-10 h-10 rounded-lg bg-white/20 flex items-center justify-center shrink-0 animate-[pulse_2s_cubic-bezier(0.4,0,0.6,1)_infinite]">
              <Smartphone className="w-5 h-5 text-primary-foreground" />
            </div>
            <div className="min-w-0">
              <p className="font-semibold text-primary-foreground text-sm sm:text-base">
                Install Gamify App
              </p>
              <p className="text-primary-foreground/80 text-xs sm:text-sm truncate">
                Quick access & offline support
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2 shrink-0">
            <Button
              onClick={handleInstall}
              size="sm"
              className="bg-white text-primary hover:bg-white/90 font-medium shadow-[0_0_15px_rgba(255,255,255,0.5)] hover:shadow-[0_0_20px_rgba(255,255,255,0.7)] transition-shadow duration-300"
            >
              <Download className="w-4 h-4 mr-1.5" />
              <span className="hidden sm:inline">Install</span>
              <span className="sm:hidden">Get</span>
            </Button>
            <button
              onClick={handleDismiss}
              className="p-2 text-primary-foreground/80 hover:text-primary-foreground hover:bg-white/10 rounded-lg transition-colors"
              aria-label="Dismiss install banner"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
